// Entry QR print source adapter for single and grouped registration signs.
// Loads entries in legacy registration order, keeps modern/legacy category IDs,
// and supplies category filters and vote URLs to the shared QrPrintView.
// Shared assets, sizes, previews, content toggles, pagination, and printing live
// in useQrPrintAssets/QrPrintView; existing routes and print CSS stay compatible.
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { useParams } from "react-router-dom";
import { db } from "../../firebase";
import { useCategories } from "../../hooks/use_categories";
import { useEventContext } from "../../hooks/use_event_context";
import { useQrPrintAssets } from "../../hooks/use_qr_print_assets";
import { logger } from "../../utils/logger";
import FullPageSpinner from "../../components/FullPageSpinner";
import QrPrintView from "../../components/QrPrintView";

export default function QrPrint() {
  const { carId } = useParams();
  const { eventId, eventslug, eventDoc, eventLoading, ready } = useEventContext({ redirectIfNotFound: true });
  const { categories, categoriesMap, loading: categoriesLoading } = useCategories(ready ? eventId : null);
  const assets = useQrPrintAssets(ready ? eventId : null);
  const [data, setData] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const sourceKey = `${eventId}:${carId || "all"}`;

  useEffect(() => {
    if (!ready || !eventId) return;
    let active = true;
    setData(null);
    const load = async () => {
      try {
        let records;
        if (carId) {
          let snapshot = await getDoc(doc(db, "events", eventId, "cars", carId));
          if (!snapshot?.exists()) {
            const matches = await getDocs(query(
              collection(db, "events", eventId, "cars"),
              where("entrySlug", "==", carId),
              limit(1)
            ));
            snapshot = matches.docs[0] || null;
          }
          records = snapshot?.exists() ? [{ ...snapshot.data(), id: snapshot.id, cid: snapshot.id }] : [];
        } else {
          const snapshot = await getDocs(query(collection(db, "events", eventId, "cars"), orderBy("regcnt", "desc")));
          records = snapshot.docs.map((record) => ({ ...record.data(), id: record.id, cid: record.id }));
        }
        if (active) setData({ key: sourceKey, records, error: "" });
      } catch (error) {
        logger.error("QR Print failed to load entries", { value: error });
        if (active) setData({ key: sourceKey, records: [], error: "Error loading entries." });
      }
    };
    load();
    return () => { active = false; };
  }, [ready, eventId, carId, sourceKey]);

  const cards = useMemo(() => (data?.records || []).map((car) => ({
    id: car.id, car, url: `${window.location.origin}/${eventslug}/vote/${car.entrySlug || car.id}`,
  })), [data, eventslug]);
  const filteredCards = useMemo(() => carId || !filterCategory ? cards : cards.filter(({ car }) => {
    const category = categories.find((candidate) => String(candidate.id) === filterCategory);
    return String(car.catid) === filterCategory || (category && String(car.catid) === String(category.firestoreId));
  }), [cards, carId, filterCategory, categories]);

  if (!ready || eventLoading || categoriesLoading || assets.loading || data?.key !== sourceKey) {
    return <FullPageSpinner message="Loading QR Codes..." />;
  }
  if (data.error || assets.error) return <div className="container mt-5 alert alert-danger" role="alert">{data.error || assets.error}</div>;
  if (carId && !cards.length) return <div className="container mt-5 alert alert-info">Entry not found.</div>;

  return <QrPrintView key={sourceKey} cards={cards} filteredCards={filteredCards} single={!!carId}
    description={carId ? "Print QR code for this entry" : "Print QR codes for registered entries"}
    assets={assets} eventName={eventDoc?.name} categoriesMap={categoriesMap}
    onResetFilters={() => setFilterCategory("")}
    filters={<div className="col-md-6 mb-2">
      <label className="form-label" htmlFor="qr-category-filter">Filter by Category</label>
      <select id="qr-category-filter" className="form-control" value={filterCategory} onChange={(event) => setFilterCategory(event.target.value)}>
        <option value="">All</option>
        {categories.map((category) => <option key={category.firestoreId || category.id} value={category.id}>{category.name}</option>)}
      </select>
    </div>}
  />;
}
